import type { TourStep } from '../components/GuidedTour';

/**
 * What the help button walks through, screen by screen.
 *
 * Each `target` matches a `data-tour` attribute on the screen. A step whose
 * section is not showing is skipped, so these can describe everything a
 * screen may hold without checking which of it is on screen right now.
 */
const SINGLE_DAY: TourStep[] = [
  {
    title: 'Welcome to TravPlaces',
    body: 'This screen plans a single day: where it starts, where it ends, and what it stops at '
      + 'along the way. Here is a quick look at each part.',
  },
  {
    target: 'nav',
    title: 'Sections',
    body: 'Plan is where you are now. Saved trips and Admin, where your build offers them, '
      + 'sit beside it.',
  },
  {
    target: 'plan-kind',
    title: 'One day or several',
    body: 'Single day plans one outing from departure to return. Multi-day spreads sites across '
      + 'several days without covering any of them twice.',
  },
  {
    target: 'city',
    title: 'City',
    body: 'Pick the city to plan in. The number beside each one is how many places it knows about.',
  },
  {
    target: 'theme',
    title: 'Mission emphasis',
    body: 'Optional. An emphasis fills in the kinds of sites to visit and the areas to keep clear of '
      + 'for you, so you do not have to choose them one by one.',
  },
  {
    target: 'transport',
    title: 'Transport',
    body: 'How the day is travelled. Hover an icon to see its name.',
  },
  {
    target: 'endpoints',
    title: 'Start and destination',
    body: 'Search for a place, or use the location button to start from where you are. You can '
      + 'also right-click the map to set either end.',
  },
  {
    target: 'time',
    title: 'Time',
    body: 'When the day starts and how long it has. The planner fits as many stops as the time '
      + 'allows.',
  },
  {
    target: 'visit',
    title: 'Sites to visit',
    body: 'Kinds of place to stop at, or particular places that must be on the route.',
  },
  {
    target: 'avoid',
    title: 'Exclusion zones',
    body: 'Places or kinds of place the route should keep its distance from.',
  },
  {
    target: 'extras',
    title: 'Rest stops and constraints',
    body: 'Lunch and coffee breaks, and other limits on the day.',
  },
  {
    target: 'plan',
    title: 'Plan the route',
    body: 'Works out the route once a start (and destination) is set. Change anything afterwards '
      + 'and plan again.',
  },
  {
    target: 'map',
    title: 'The map',
    body: 'Right-click anywhere to mark a spot as a start, destination, stop or area to avoid. Once a '
      + 'route is drawn, drag its markers to move them.',
  },
  {
    target: 'map-only',
    title: 'Map only',
    body: 'Hide the side panel to give the map the whole screen, and bring it back the same way.',
  },
  {
    target: 'assistant',
    title: 'Plan by asking',
    body: 'Describe the day in your own words and the assistant fills in the form for you.',
  },
  {
    target: 'help',
    title: 'That is it',
    body: 'Press this button any time to see the tour again.',
  },
];

/** The tour for the screen at `pathname`, or null if it has none yet. */
export function tourFor(pathname: string): TourStep[] | null {
  if (pathname === '/plan' || pathname === '/plan/day') return SINGLE_DAY;
  return null;
}
